import React, { useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useAuditActions } from '../hooks/useAudit';
import { 
  Play, 
  Square, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Users,
  Activity
} from 'lucide-react';

interface InventorySessionProps {
  sessionId?: string;
  onSessionStart?: (sessionId: string) => void;
  onSessionEnd?: (sessionId: string) => void;
  onValidateDiscrepancies?: (sessionId: string, discrepancies: any[]) => void;
}

const InventorySession: React.FC<InventorySessionProps> = ({
  sessionId,
  onSessionStart,
  onSessionEnd,
  onValidateDiscrepancies
}) => {
  const permissions = usePermissions();
  const auditActions = useAuditActions();
  const [isStarting, setIsStarting] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const handleStartSession = async () => {
    if (!permissions.canStartInventorySession) return;
    
    setIsStarting(true);
    try {
      const newSessionId = `session_${Date.now()}`;
      
      // Logger l'action
      await auditActions.logCreate('ITEM', newSessionId, {
        type: 'INVENTORY_SESSION',
        status: 'STARTED',
        timestamp: new Date().toISOString()
      }, 'UI');
      
      onSessionStart?.(newSessionId);
    } catch (error) {
      console.error('Erreur lors du démarrage de la session:', error);
    } finally {
      setIsStarting(false);
    }
  };

  const handleEndSession = async () => {
    if (!permissions.canCloseInventorySession) return;
    
    setIsEnding(true);
    try {
      // Logger l'action
      await auditActions.logUpdate('ITEM', sessionId!, {
        type: 'INVENTORY_SESSION',
        status: 'ENDED',
        timestamp: new Date().toISOString()
      }, 'UI');
      
      onSessionEnd?.(sessionId!);
    } catch (error) {
      console.error('Erreur lors de la fermeture de la session:', error);
    } finally {
      setIsEnding(false);
    }
  };

  const handleValidateDiscrepancies = async () => {
    if (!permissions.canValidateDiscrepancies) return;
    
    setIsValidating(true);
    try {
      // Simuler la validation des écarts
      const discrepancies = [
        { itemId: '1', expected: 5, found: 3, difference: -2 },
        { itemId: '2', expected: 2, found: 4, difference: 2 }
      ];
      
      // Logger l'action
      await auditActions.logUpdate('ITEM', sessionId!, {
        type: 'DISCREPANCY_VALIDATION',
        discrepancies,
        timestamp: new Date().toISOString()
      }, 'UI');
      
      onValidateDiscrepancies?.(sessionId!, discrepancies);
    } catch (error) {
      console.error('Erreur lors de la validation des écarts:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const getSessionStatus = () => {
    if (sessionId) {
      return {
        status: 'active',
        label: 'Session Active',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    }
    return {
      status: 'inactive',
      label: 'Aucune Session',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    };
  };

  const sessionStatus = getSessionStatus();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Activity className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Session d'Inventaire</h3>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-sm font-medium border ${sessionStatus.bgColor} ${sessionStatus.borderColor} ${sessionStatus.color}`}>
          {sessionStatus.label}
        </div>
      </div>

      {/* Explication */}
      <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg">
        <h4 className="text-sm font-semibold text-teal-900 mb-2">📦 Sessions d'inventaire physique</h4>
        <p className="text-sm text-teal-800 mb-2">
          Les <strong>sessions d'inventaire</strong> permettent de faire des <strong>comptages physiques</strong> périodiques 
          et de valider les quantités réelles par rapport aux quantités théoriques.
        </p>
        <ul className="text-xs text-teal-700 list-disc list-inside space-y-1">
          <li><strong>Démarrer</strong> une session pour commencer le comptage</li>
          <li><strong>Compter</strong> les objets physiquement</li>
          <li><strong>Valider</strong> les écarts entre théorie et réalité</li>
          <li><strong>Fermer</strong> la session pour finaliser</li>
        </ul>
      </div>

      {/* Informations de la session */}
      {sessionId && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Session ID: {sessionId}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Users className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-700">Inventoristes: 2 actifs</span>
          </div>
        </div>
      )}

      {/* Actions disponibles */}
      <div className="space-y-3">
        {/* Démarrer une session */}
        {!sessionId && (
          <button
            onClick={handleStartSession}
            disabled={!permissions.canStartInventorySession || isStarting}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              permissions.canStartInventorySession && !isStarting
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isStarting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Play className="h-4 w-4" />
            )}
            <span>
              {isStarting ? 'Démarrage...' : 'Démarrer une Session'}
            </span>
          </button>
        )}

        {/* Fermer la session */}
        {sessionId && (
          <button
            onClick={handleEndSession}
            disabled={!permissions.canCloseInventorySession || isEnding}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              permissions.canCloseInventorySession && !isEnding
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isEnding ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Square className="h-4 w-4" />
            )}
            <span>
              {isEnding ? 'Fermeture...' : 'Fermer la Session'}
            </span>
          </button>
        )}

        {/* Valider les écarts */}
        {sessionId && (
          <button
            onClick={handleValidateDiscrepancies}
            disabled={!permissions.canValidateDiscrepancies || isValidating}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              permissions.canValidateDiscrepancies && !isValidating
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isValidating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            <span>
              {isValidating ? 'Validation...' : 'Valider les Écarts'}
            </span>
          </button>
        )}
      </div>

      {/* Messages d'information selon les permissions */}
      {!permissions.canStartInventorySession && !sessionId && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              Vous n'avez pas l'autorisation de démarrer une session d'inventaire.
            </span>
          </div>
        </div>
      )}

      {!permissions.canCloseInventorySession && sessionId && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              Vous n'avez pas l'autorisation de fermer cette session d'inventaire.
            </span>
          </div>
        </div>
      )}

      {!permissions.canValidateDiscrepancies && sessionId && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              Vous n'avez pas l'autorisation de valider les écarts d'inventaire.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySession;
